/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  TextField,
  IconButton,
  FormControlLabel,
  Radio,
  RadioGroup,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useCuffaStore } from 'stores/cuffa/cuffa.store';
import { ROUTES } from 'routes/constants';
import { CuffaValueAddedSealEnum } from 'interfaces/cuffa.interface';
import { CuffaAPI } from 'api/cuffa/cuffa.api';

interface IUpdateValueAddedSealsDto {
  has_value_added_seals: boolean;
  value_added_seals?: string[];
  other_value_added_seals?: string[]; // ✅ agora só strings
}

const selosPadrao = Object.values(CuffaValueAddedSealEnum).filter(
  (selo) => selo !== CuffaValueAddedSealEnum.OUTRO
);

export interface ISelosFormValues {
  possuiSelo: string;
  selosSelecionados: string[];
  outrosSelos: string[];
  nomesSelos: Record<string, string>;
  novoSelo?: string;
}

export function buildPayload(values: ISelosFormValues) {
  const payload: IUpdateValueAddedSealsDto = {
    has_value_added_seals: values.possuiSelo === 'sim',
  };

  if (values.possuiSelo === 'sim') {
    payload.value_added_seals = values.selosSelecionados.filter(
      selo => selo !== CuffaValueAddedSealEnum.OUTRO
    );

    const outrosParaEnviar = values.selosSelecionados.includes(CuffaValueAddedSealEnum.OUTRO)
      ? values.outrosSelos
      : [];

    payload.other_value_added_seals = outrosParaEnviar;
  }

  return payload;
}

export function mapSavedDataToFormValues(savedData: any, initialValues: ISelosFormValues): ISelosFormValues {
  const newFormValues: ISelosFormValues = { ...initialValues } as ISelosFormValues;

  if (!savedData) return newFormValues;

  newFormValues.possuiSelo = savedData.has_value_added_seals ? 'sim' : 'nao';

  if (savedData.has_value_added_seals) {
    newFormValues.selosSelecionados = savedData.value_added_seals || [];

    let outrosSalvos: string[] = [];
    if (Array.isArray(savedData.other_value_added_seals)) {
      outrosSalvos = savedData.other_value_added_seals;
    }

    if (outrosSalvos.length > 0) {
      newFormValues.selosSelecionados = [...newFormValues.selosSelecionados, CuffaValueAddedSealEnum.OUTRO];
    }

    newFormValues.outrosSelos = outrosSalvos;
    newFormValues.nomesSelos = {};
  }

  return newFormValues;
}

export const SelosAgregacaoValor: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const { salvarDadosParciais } = useCuffaStore();

  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const advanceNext = useRef(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const formik = useFormik({
    initialValues: {
      possuiSelo: '',
      selosSelecionados: [] as string[],
      outrosSelos: [] as string[],   // ✅ continua guardando strings
      nomesSelos: {} as Record<string, string>, // usado só no front
      novoSelo: '',
    },
    validationSchema: Yup.object().shape({
      possuiSelo: Yup.string().required('Este campo é obrigatório'),
    }),
    onSubmit: async (values) => {
      if (!uuid) {
        setSnackbar({ open: true, message: 'UUID não encontrado. Não é possível salvar.', severity: 'error' });
        return;
      }
      if (values.possuiSelo === 'sim' && values.selosSelecionados.length === 0 && values.outrosSelos.length === 0) {
        setSnackbar({ open: true, message: 'Selecione ou adicione pelo menos um selo.', severity: 'error' });
        return;
      }

      setSaving(true);
      const payload = buildPayload(values);

      try {
        await CuffaAPI.updateValueAddedSeals(uuid, payload);
        const dadosParaSalvar = { ...values };
        delete (dadosParaSalvar as any).novoSelo;

        salvarDadosParciais({ selosAgregacaoValor: dadosParaSalvar });
        setSnackbar({ open: true, message: 'Dados salvos com sucesso!', severity: 'success' });

        if (advanceNext.current) {
          const nextRoute = ROUTES.CANAIS_COMERCIALIZACAO_CUFFA_INCLUIR.replace(':uuid', uuid);
          navigate(nextRoute);
        }
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error.message || 'Erro ao salvar dados.';
        setSnackbar({ open: true, message: Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage, severity: 'error' });
      } finally {
        setSaving(false);
        advanceNext.current = false;
      }
    },
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!uuid) {
        setSnackbar({ open: true, message: 'UUID não encontrado.', severity: 'error' });
        setInitialLoading(false);
        return;
      }
      // Primeiro, tentar restaurar do localStorage (chave específica do uuid, depois chave global)
      try {
        const storageKeys: string[] = [];
        if (uuid) storageKeys.push(`cuffa-storage-${uuid}`);
        storageKeys.push('cuffa-storage');

        for (const storageKey of storageKeys) {
          try {
            const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
            if (!raw) continue;
            const parsed = JSON.parse(raw);
            const saved = parsed?.state?.dadosParciais?.selosAgregacaoValor;
            if (!saved) continue;

            const newFormValues = { ...formik.initialValues };

            // aceita tanto formato front (possuiSelo) quanto formato API (has_value_added_seals)
            if (typeof saved.possuiSelo === 'string') {
              newFormValues.possuiSelo = saved.possuiSelo === 'sim' ? 'sim' : 'nao';
            } else if (typeof saved.has_value_added_seals === 'boolean') {
              newFormValues.possuiSelo = saved.has_value_added_seals ? 'sim' : 'nao';
            }

            // selos selecionados: pode vir em `selosSelecionados` (front) ou `value_added_seals` (api)
            if (Array.isArray(saved.selosSelecionados)) {
              newFormValues.selosSelecionados = saved.selosSelecionados;
            } else if (Array.isArray(saved.value_added_seals)) {
              newFormValues.selosSelecionados = saved.value_added_seals;
            }

            // outros selos: pode ser array em `outrosSelos` ou `other_value_added_seals` (array or object)
            if (Array.isArray(saved.outrosSelos)) {
              newFormValues.outrosSelos = saved.outrosSelos;
            } else if (Array.isArray(saved.other_value_added_seals)) {
              newFormValues.outrosSelos = saved.other_value_added_seals;
            } else if (saved.other_value_added_seals && typeof saved.other_value_added_seals === 'object') {
              newFormValues.outrosSelos = Object.keys(saved.other_value_added_seals || {});
            }

            // nomes dos selos (front) ou o objeto da API com nomes
            newFormValues.nomesSelos = saved.nomesSelos || saved.other_value_added_seals || {};

            // Se houver outros selos, garantir que o checkbox OUTRO esteja presente
            if ((newFormValues.outrosSelos || []).length > 0 && !newFormValues.selosSelecionados.includes(CuffaValueAddedSealEnum.OUTRO)) {
              newFormValues.selosSelecionados = [...(newFormValues.selosSelecionados || []), CuffaValueAddedSealEnum.OUTRO];
            }

            formik.setValues(newFormValues);
            setInitialLoading(false);
            return;
          } catch (e) {
            console.warn(`Falha ao parsear ${storageKey} ao restaurar selosAgregacaoValor:`, e);
            continue;
          }
        }
      } catch (e) {
        console.warn('Erro ao acessar localStorage cuffa-storage ao restaurar selosAgregacaoValor', e);
      }

      // Se não houve dados no storage, busca pela API como antes
      try {
        const response = await CuffaAPI.getDataByID(uuid);
        const savedData = response.dados;

        if (savedData) {
          const newFormValues = { ...formik.initialValues };

          newFormValues.possuiSelo = savedData.has_value_added_seals ? 'sim' : 'nao';

          if (savedData.has_value_added_seals) {
            newFormValues.selosSelecionados = savedData.value_added_seals || [];

            let outrosSalvos: string[] = [];
            if (Array.isArray(savedData.other_value_added_seals)) {
              outrosSalvos = savedData.other_value_added_seals;
            }

            if (outrosSalvos.length > 0) {
              newFormValues.selosSelecionados.push(CuffaValueAddedSealEnum.OUTRO);
            }

            newFormValues.outrosSelos = outrosSalvos;
            newFormValues.nomesSelos = {};
          }

          formik.setValues(newFormValues);
        }
      } catch (error) {
        console.error("Erro ao carregar dados de selos:", error);
        setSnackbar({ open: true, message: 'Erro ao carregar dados.', severity: 'error' });
      } finally {
        setInitialLoading(false);
      }
    };
    fetchInitialData();
  }, [uuid]);

  const handleCheckboxChange = (selo: string) => {
    const { selosSelecionados } = formik.values;
    const novosSelos = selosSelecionados.includes(selo)
      ? selosSelecionados.filter((s) => s !== selo)
      : [...selosSelecionados, selo];
    formik.setFieldValue('selosSelecionados', novosSelos);
  };

  const handleAdicionarOutroSelo = () => {
    const novo = formik.values.novoSelo.trim().toUpperCase();
    if (!novo || formik.values.outrosSelos.includes(novo)) return;
    formik.setFieldValue('outrosSelos', [...formik.values.outrosSelos, novo]);
    formik.setFieldValue('novoSelo', '');
    if (!formik.values.selosSelecionados.includes(CuffaValueAddedSealEnum.OUTRO)) {
      formik.setFieldValue('selosSelecionados', [...formik.values.selosSelecionados, CuffaValueAddedSealEnum.OUTRO]);
    }
  };

  const handleRemoverOutroSelo = (index: number) => {
    const seloRemovido = formik.values.outrosSelos[index];
    const novosOutrosSelos = formik.values.outrosSelos.filter((_, i) => i !== index);
    const novosNomesSelos = { ...formik.values.nomesSelos };
    delete novosNomesSelos[seloRemovido];

    formik.setFieldValue('outrosSelos', novosOutrosSelos);
    formik.setFieldValue('nomesSelos', novosNomesSelos);

    if (novosOutrosSelos.length === 0) {
      formik.setFieldValue('selosSelecionados', formik.values.selosSelecionados.filter(s => s !== CuffaValueAddedSealEnum.OUTRO));
    }
  };

  const handleSave = () => {
    advanceNext.current = false;
    formik.handleSubmit();
  };

  const handleAdvance = () => {
    advanceNext.current = true;
    formik.handleSubmit();
  };

  if (initialLoading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Selos de Agregação de Valor" showBackButton />
        <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress />
          <Typography ml={2}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Selos de Agregação de Valor" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Selos de Agregação de Valor" width="100%">
            <Box marginBottom="2rem">
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                A unidade familiar possui selo de agregação de valor atrelado à comercialização? *
              </Typography>
              <RadioGroup row name="possuiSelo" value={formik.values.possuiSelo} onChange={formik.handleChange}>
                <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                <FormControlLabel value="nao" control={<Radio />} label="Não" />
              </RadioGroup>
              {formik.touched.possuiSelo && formik.errors.possuiSelo && (
                <Typography color="error" variant="caption">{formik.errors.possuiSelo}</Typography>
              )}
            </Box>

            {formik.values.possuiSelo === 'sim' && (
              <Box sx={{ backgroundColor: '#f5faff', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem' }}>
                <Typography fontWeight="bold" mb={2}>Marque todos os selos que se aplicam:</Typography>
                <Grid container spacing={2}>
                  {selosPadrao.map((selo) => (
                    <Grid item xs={12} sm={6} key={selo}>
                      <FormControlLabel
                        control={<Checkbox checked={formik.values.selosSelecionados.includes(selo)} onChange={() => handleCheckboxChange(selo)} />}
                        label={selo}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Checkbox checked={formik.values.selosSelecionados.includes(CuffaValueAddedSealEnum.OUTRO)} onChange={() => handleCheckboxChange(CuffaValueAddedSealEnum.OUTRO)} />}
                      label={CuffaValueAddedSealEnum.OUTRO}
                    />
                  </Grid>
                </Grid>

                {formik.values.selosSelecionados.includes(CuffaValueAddedSealEnum.OUTRO) && (
                  <Box mt={4}>
                    <Typography fontWeight="bold" gutterBottom>Outro tipo de selo:</Typography>
                    <Box display="flex" gap={2} alignItems="center">
                      <TextField placeholder="Digite o tipo do selo" name="novoSelo" value={(formik.values.novoSelo || '').toLocaleUpperCase()} onChange={formik.handleChange} size="small" fullWidth />
                      <Button variant="outlined" onClick={handleAdicionarOutroSelo} sx={{ height: '40px', minWidth: '120px', color: 'black', borderColor: 'black', textTransform: 'none' }}>
                        + Adicionar
                      </Button>
                    </Box>
                    <Box mt={2}>
                      {formik.values.outrosSelos.map((nome, index) => (
                        <Box key={index} display="flex" alignItems="center" justifyContent="space-between" mt={1}>
                          <Box width="100%" mr={2}>
                            <Typography>{nome}</Typography>
                            <TextField
                              placeholder="Nome do selo (opcional)"
                              value={formik.values.nomesSelos[nome] || ''}
                              onChange={(e) => formik.setFieldValue(`nomesSelos.${nome}`, e.target.value.toUpperCase())}
                              fullWidth size="small" sx={{ mt: 1 }}
                            />
                          </Box>
                          <IconButton onClick={() => handleRemoverOutroSelo(index)}>
                            <Delete />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            <Box marginTop="2rem" display="flex" justifyContent="center" gap="1rem">
              <Button variant="outlined" color="secondary" onClick={() => navigate(ROUTES.INSPECAO_PRODUTOS_CUFFA_INCLUIR.replace(':uuid', uuid ?? ''))} disabled={saving}>
                Retornar
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleSave} disabled={saving}>
                {saving && !advanceNext.current ? <CircularProgress size={24} color="inherit" /> : 'Salvar'}
              </Button>
              <Button variant="contained" color="primary" onClick={handleAdvance} disabled={saving}>
                {saving && advanceNext.current ? <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} /> : null}
                Avançar
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MenuProvider>
  );
};

export default SelosAgregacaoValor;
